import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { AdminContractDetail } from '../api/client';
import { useAdminAuth } from '../auth/AuthProvider';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate, shortHash } from '../lib/format';

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { fetcher } = useAdminAuth();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'contracts', id],
    queryFn: () => fetcher<AdminContractDetail>(`/admin/api/contracts/${id}`),
    enabled: !!id,
  });

  const downloadMutation = useMutation({
    mutationFn: () =>
      fetcher<{ url: string; expires_in: number }>(
        `/admin/api/contracts/${id}/signed-document`,
      ),
    onSuccess: (r) => {
      setDownloadUrl(r.url);
      setDownloadError(null);
    },
    onError: (err) => {
      setDownloadError(err instanceof Error ? err.message : 'download failed');
    },
  });

  if (isLoading) return <div>Loading…</div>;
  if (error) return <div className="card text-red-600">{(error as Error).message}</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/contracts" className="text-sm text-slate-600 hover:underline">
          ← Back to contracts
        </Link>
      </div>
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">
          Contract <code className="text-base">{data.id.slice(0, 8)}</code>
        </h1>
        <StatusBadge status={data.status} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="card text-sm">
          <h2 className="mb-2 font-medium">Tenant</h2>
          <Link to={`/tenants/${data.account_id}`} className="text-brand hover:underline">
            {data.account_name ?? data.account_slug}
          </Link>
        </section>
        <section className="card text-sm">
          <h2 className="mb-2 font-medium">Timeline</h2>
          <ul className="space-y-1 text-slate-700">
            <li>Created: {formatDate(data.created_at)}</li>
            <li>Sent: {formatDate(data.sent_at)}</li>
            <li>Signed: {formatDate(data.signed_at)}</li>
            <li>Voided: {formatDate(data.voided_at)}</li>
            <li>Declined: {formatDate(data.declined_at)}</li>
            <li>Completed: {formatDate(data.completed_at)}</li>
          </ul>
        </section>
        <section className="card text-sm">
          <h2 className="mb-2 font-medium">Hashes</h2>
          <div>pre: <code className="text-xs">{shortHash(data.pre_sign_hash, 24)}</code></div>
          <div>post: <code className="text-xs">{shortHash(data.post_sign_hash, 24)}</code></div>
        </section>
        <section className="card text-sm space-y-2">
          <h2 className="mb-2 font-medium">Signed document</h2>
          <div>key: <code className="text-xs">{data.signed_document_key ?? '—'}</code></div>
          <button
            type="button"
            className="btn-primary"
            disabled={!data.signed_document_key || downloadMutation.isPending}
            onClick={() => downloadMutation.mutate()}
          >
            Download signed PDF
          </button>
          {downloadUrl ? (
            <div>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-brand underline"
              >
                Open download URL (audit logged)
              </a>
            </div>
          ) : null}
          {downloadError ? (
            <div className="text-sm text-red-600">{downloadError}</div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
